/* eslint-disable */
import React, { useState } from 'react';

import {
  makeStyles,
  createStyles,
  Grid,
  FormControl,
  FormLabel,
  FormHelperText,
  TextField,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import moment, { Moment } from 'moment';
import { useIntl } from 'react-intl';

const useStyles = makeStyles(() =>
  createStyles({
    inputContainer: {
      backgroundColor: '#fff',
      height: 50,
    },
    startDate: {
      paddingRight: 3,
    },
    endDate: {
      paddingLeft: 3,
    },
    formLabel: {
      color: '#444',
      marginBottom: 7,
      fontSize: 14,
      fontWeight: 700,
    },
    formControl: {
      marginBottom: 22,
    },
    helperText: {
      fontSize: 12,
      color: '#999',
      marginTop: 4,
    },
    timeInputContainer: {
      display: 'flex',
      gap: 8,
      marginTop: 8,
    },
    timeInput: {
      width: '30%',
      '& input': {
        padding: '10px',
        fontSize: '14px',
      },
    },
  })
);

type ParamTypes = {
  minDateValue: string;
  maxDateValue: string;
  minDateFilterName: string;
  maxDateFilterName: string;
  onChangeMinDate: (
    filterName: string,
    date: string | Moment | undefined
  ) => void;
  onChangeMaxDate: (
    filterName: string,
    date: string | Moment | undefined
  ) => void;
  minLabel?: string;
  maxLabel?: string;
};

const FiltersDateRangeSelector = ({
  minDateValue,
  maxDateValue,
  minDateFilterName,
  maxDateFilterName,
  onChangeMinDate,
  onChangeMaxDate,
  minLabel,
  maxLabel,
}: ParamTypes) => {
  const classes = useStyles();
  const intl = useIntl();

  // State for time inputs
  const [minTime, setMinTime] = useState({ hours: '', minutes: '', seconds: '' });
  const [maxTime, setMaxTime] = useState({ hours: '', minutes: '', seconds: '' });

  // Initialize time from existing values if they exist
  React.useEffect(() => {
    if (minDateValue) {
      const date = moment(minDateValue);

      setMinTime({
        hours: date.format('HH'),
        minutes: date.format('mm'),
        seconds: date.format('ss'),
      });
    }
  }, [minDateValue]);

  React.useEffect(() => {
    if (maxDateValue) {
      const date = moment(maxDateValue);

      setMaxTime({
        hours: date.format('HH'),
        minutes: date.format('mm'),
        seconds: date.format('ss'),
      });
    }
  }, [maxDateValue]);

  const handleMinDateChange = (date: Moment | null) => {
    if (date) {
      const combinedDateTime = combineDateTime(date, minTime);
      onChangeMinDate(minDateFilterName, combinedDateTime);
    } else {
      onChangeMinDate(minDateFilterName, undefined);
    }
  };

  const handleMaxDateChange = (date: Moment | null) => {
    if (date) {
      const combinedDateTime = combineDateTime(date, maxTime);
      onChangeMaxDate(maxDateFilterName, combinedDateTime);
    } else {
      onChangeMaxDate(maxDateFilterName, undefined);
    }
  };

  const handleMinTimeChange = (field: 'hours' | 'minutes' | 'seconds', value: string) => {
    const newMinTime = { ...minTime, [field]: value };
    setMinTime(newMinTime);

    if (minDateValue) {
      const date = moment(minDateValue);
      const combinedDateTime = combineDateTime(date, newMinTime);
      onChangeMinDate(minDateFilterName, combinedDateTime);
    }
  };

  const handleMaxTimeChange = (field: 'hours' | 'minutes' | 'seconds', value: string) => {
    const newMaxTime = { ...maxTime, [field]: value };
    setMaxTime(newMaxTime);

    if (maxDateValue) {
      const date = moment(maxDateValue);
      const combinedDateTime = combineDateTime(date, newMaxTime);
      onChangeMaxDate(maxDateFilterName, combinedDateTime);
    }
  };

  const combineDateTime = (date: Moment, time: { hours: string; minutes: string; seconds: string }) => {
    const hours = time.hours.padStart(2, '0');
    const minutes = time.minutes.padStart(2, '0');
    const seconds = time.seconds.padStart(2, '0');
    const timeString = `${hours}:${minutes}:${seconds}`;

    return `${date.format('YYYY-MM-DD')  } ${  timeString}`;
  };

  return (
    <Grid container>
      <Grid item sm={6} className={classes.startDate}>
        <FormControl fullWidth className={classes.formControl}>
          <FormLabel className={classes.formLabel}>
            {minLabel || intl.formatMessage({ id: 'fromDate' })}
          </FormLabel>

          <DatePicker
            variant="inline"
            format="DD/MM/yyyy"
            id="date-picker-inline"
            value={minDateValue ? moment(minDateValue) : null}
            onChange={handleMinDateChange}
            disableFuture
            style={{ backgroundColor: '#fff' }}
            inputVariant="outlined"
            InputProps={{
              className: classes.inputContainer,
              style: {
                color: minDateValue ? '#000' : '#aaa',
              },
            }}
            emptyLabel={moment().format('DD/MM/YYYY')}
            maxDate={maxDateValue ? moment(maxDateValue) : undefined}
            disableToolbar
            autoOk
          />

          <div className={classes.timeInputContainer}>
            <TextField
              className={classes.timeInput}
              label="HH"
              variant="outlined"
              size="small"
              value={minTime.hours}
              onChange={(e) => handleMinTimeChange('hours', e.target.value)}
              inputProps={{ maxLength: 2, pattern: '[0-9]*' }}
            />

            <TextField
              className={classes.timeInput}
              label="MM"
              variant="outlined"
              size="small"
              value={minTime.minutes}
              onChange={(e) => handleMinTimeChange('minutes', e.target.value)}
              inputProps={{ maxLength: 2, pattern: '[0-9]*' }}
            />

            <TextField
              className={classes.timeInput}
              label="SS"
              variant="outlined"
              size="small"
              value={minTime.seconds}
              onChange={(e) => handleMinTimeChange('seconds', e.target.value)}
              inputProps={{ maxLength: 2, pattern: '[0-9]*' }}
            />
          </div>

        </FormControl>
      </Grid>

      <Grid item sm={6} className={classes.endDate}>
        <FormControl fullWidth className={classes.formControl}>
          <FormLabel className={classes.formLabel}>
            {maxLabel || intl.formatMessage({ id: 'toDate' })}
          </FormLabel>

          <DatePicker
            variant="inline"
            format="DD/MM/yyyy"
            id="date-picker-inline"
            value={maxDateValue ? moment(maxDateValue) : null}
            onChange={handleMaxDateChange}
            disableFuture
            style={{ backgroundColor: '#fff' }}
            inputVariant="outlined"
            InputProps={{
              className: classes.inputContainer,
              style: {
                color: maxDateValue ? '#000' : '#aaa',
              },
            }}
            minDate={minDateValue ? moment(minDateValue) : undefined}
            emptyLabel={moment().format('DD/MM/YYYY')}
            disableToolbar
            autoOk
          />

          <div className={classes.timeInputContainer}>
            <TextField
              className={classes.timeInput}
              label="HH"
              variant="outlined"
              size="small"
              value={maxTime.hours}
              onChange={(e) => handleMaxTimeChange('hours', e.target.value)}
              inputProps={{ maxLength: 2, pattern: '[0-9]*' }}
            />

            <TextField
              className={classes.timeInput}
              label="MM"
              variant="outlined"
              size="small"
              value={maxTime.minutes}
              onChange={(e) => handleMaxTimeChange('minutes', e.target.value)}
              inputProps={{ maxLength: 2, pattern: '[0-9]*' }}
            />

            <TextField
              className={classes.timeInput}
              label="SS"
              variant="outlined"
              size="small"
              value={maxTime.seconds}
              onChange={(e) => handleMaxTimeChange('seconds', e.target.value)}
              inputProps={{ maxLength: 2, pattern: '[0-9]*' }}
            />
          </div>

        </FormControl>
      </Grid>
    </Grid>
  );
};

export { FiltersDateRangeSelector };
